import React, { useState } from 'react';
import { CheckCircle, AlertCircle, XCircle, Clock, ChevronDown, ChevronUp } from 'lucide-react';

// Read-only timeline of the last 10 sync runs. Backed by
// ExternalIntegrationStatus.recentRuns (trimmed to 10 by the BE).

const statusIcon = {
  success: <CheckCircle className="tw-w-4 tw-h-4 tw-text-green-600 tw-flex-shrink-0" />,
  partial: <AlertCircle className="tw-w-4 tw-h-4 tw-text-amber-500 tw-flex-shrink-0" />,
  error: <XCircle className="tw-w-4 tw-h-4 tw-text-red-600 tw-flex-shrink-0" />,
};

const RunHistory = ({ status }) => {
  const runs = (status?.recentRuns ?? []).slice().reverse(); // newest first
  if (runs.length === 0) {
    return (
      <div className="tw-p-4 tw-bg-gray-50 tw-rounded-lg tw-text-sm tw-text-gray-500 tw-flex tw-items-center tw-gap-2">
        <Clock className="tw-w-4 tw-h-4" /> No runs yet — click <strong>Sync now</strong> to start.
      </div>
    );
  }
  return (
    <div className="tw-space-y-1">
      {runs.map((r, i) => (
        <RunRow key={i} run={r} />
      ))}
    </div>
  );
};

const RunRow = ({ run }) => {
  const [open, setOpen] = useState(false);
  const c = run.counts || {};
  const when = run.at ? new Date(run.at).toLocaleString() : '';
  const summary = summaryText(c);
  return (
    <div className="tw-bg-white tw-border tw-border-gray-100 tw-rounded-lg tw-px-3 tw-py-2 tw-text-sm">
      <div className="tw-flex tw-items-center tw-gap-3">
        {statusIcon[run.status] ?? <Clock className="tw-w-4 tw-h-4" />}
        <div className="tw-flex-1 tw-min-w-0">
          <div className="tw-flex tw-items-baseline tw-gap-2">
            <span className="tw-font-medium tw-text-gray-800 tw-capitalize">{run.status}</span>
            <span className="tw-text-xs tw-text-gray-400">{when}</span>
          </div>
          <p className="tw-text-xs tw-text-gray-500 tw-mt-0.5 tw-mb-0 tw-truncate">{summary}</p>
        </div>
        {run.error && (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="tw-bg-transparent tw-border-0 tw-text-gray-400 hover:tw-text-gray-700 tw-cursor-pointer tw-p-1"
            aria-label="Toggle error details"
          >
            {open ? <ChevronUp className="tw-w-4 tw-h-4" /> : <ChevronDown className="tw-w-4 tw-h-4" />}
          </button>
        )}
      </div>
      {open && run.error && (
        <pre className="tw-mt-2 tw-p-2 tw-bg-red-50 tw-rounded tw-text-xs tw-text-red-700 tw-whitespace-pre-wrap tw-overflow-x-auto tw-m-0">
          {run.error}
        </pre>
      )}
    </div>
  );
};

const summaryText = (c) => {
  const parts = [];
  if (c.groupsProcessed != null) parts.push(`${c.groupsProcessed} vessel${plural(c.groupsProcessed)}`);
  if (c.imported) parts.push(`${c.imported} imported`);
  if (c.cached) parts.push(`${c.cached} already synced`);
  if (c.disabled) parts.push(`${c.disabled} removed`);
  if (c.duplicateErrors) parts.push(`${c.duplicateErrors} duplicate err`);
  return parts.length === 0 ? '—' : parts.join(' · ');
};

const plural = (n) => (n === 1 ? '' : 's');

export default RunHistory;
